import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui";
import { describePhase3Progress, phase3OverallPercent } from "../lib/phase3Progress";
import type { Phase3Progress } from "../lib/phase3Progress";
import { Icon } from "./Icon";
import { Phase1ProgressBar } from "./Phase1ProgressBar";

type Props = {
  runId: string;
  targetName?: string;
  contractorName?: string;
  jobStatus?: string;
  progress: Phase3Progress | null;
  error?: string;
  expanded: boolean;
  onToggle: () => void;
  canStop?: boolean;
  stopping?: boolean;
  onStop?: () => void;
  children?: ReactNode;
  /** When true, omit outer chrome (parent supplies border/shadow). */
  embedded?: boolean;
};

export function Phase3ProgressDock({
  runId,
  targetName = "—",
  contractorName = "",
  jobStatus = "",
  progress,
  error,
  expanded,
  onToggle,
  canStop = false,
  stopping = false,
  onStop,
  children,
  embedded = false,
}: Props) {
  const copy = describePhase3Progress(progress, jobStatus);
  const overallPct = phase3OverallPercent(progress);
  const showBar = !error && jobStatus !== "pending";

  return (
    <div
      className={`shrink-0 flex flex-col ${
        embedded
          ? "border-b border-outline-variant/60"
          : "border-t border-outline-variant bg-surface-container"
      } ${expanded && !embedded ? "shadow-[0_-4px_24px_rgba(0,0,0,0.35)] max-h-80" : ""}`}
    >
      <div className="flex items-stretch border-b border-outline-variant/40">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 px-4 py-3 flex items-center gap-3 text-left hover:bg-surface-container-high transition-colors"
          aria-expanded={expanded}
        >
          {!error && <span className="orion-spinner w-5 h-5 shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-on-surface truncate">
              {error ? "PDR synthesis failed" : copy.headline}
              {targetName !== "—" ? (
                <span className="font-normal text-on-surface-variant"> · {targetName}</span>
              ) : null}
              {contractorName ? (
                <span className="font-normal text-primary"> · {contractorName}</span>
              ) : null}
            </p>
            <p className="text-xs font-data-mono text-on-surface-variant truncate">
              {error || copy.detail || "Discovering and synthesizing PDR projects…"}
              {expanded ? " · click to collapse" : " · click to expand live progress"}
            </p>
            {showBar ? (
              <div className="mt-2 max-w-xl flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Phase1ProgressBar percent={overallPct} compact label="" />
                </div>
                <span className="text-[10px] text-on-surface-variant shrink-0 tabular-nums">
                  {Math.round(overallPct)}%
                </span>
              </div>
            ) : null}
          </div>
          <Link
            to={`/logs?run=${encodeURIComponent(runId)}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary text-[10px] uppercase tracking-widest hover:underline shrink-0 px-2"
          >
            Log
          </Link>
          <Icon
            name={expanded ? "expand_more" : "expand_less"}
            className="text-on-surface-variant shrink-0"
          />
        </button>
        {canStop && onStop ? (
          <Button
            variant="danger"
            onClick={onStop}
            disabled={stopping}
            className="shrink-0 rounded-none h-full px-4 py-3 border-l border-outline-variant bg-error/10 hover:bg-error/20"
            title="Stop this PDR synthesis run"
          >
            {stopping ? "Stopping…" : "Stop job"}
          </Button>
        ) : null}
      </div>
      {expanded ? (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">{children}</div>
      ) : null}
    </div>
  );
}
